import { useEffect, useState } from "react";
import { Check, X, Star, Plus, Trash2, Pencil } from "lucide-react";
import StaffLayout from "@/components/staff/StaffLayout";
import { api } from "@/lib/api";
import { toast } from "sonner";

const TABS = [
  { key: "pending", label: "Pending" },
  { key: "approved", label: "Approved" },
  { key: "rejected", label: "Rejected" },
  { key: "all", label: "All" },
];

export default function StaffReviewsPage() {
  const [tab, setTab] = useState("pending");
  const [reviews, setReviews] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [editing, setEditing] = useState(null);

  function load() {
    const q = tab === "all" ? {} : { status: tab };
    api.get("/admin/reviews", { params: q }).then((r) => setReviews(r.data));
  }
  useEffect(load, [tab]);

  async function moderate(id, fields) {
    await api.put(`/admin/reviews/${id}`, fields);
    toast.success("Updated");
    load();
  }

  async function del(id) {
    if (!confirm("Delete review?")) return;
    await api.delete(`/admin/reviews/${id}`);
    toast.success("Deleted");
    load();
  }

  return (
    <StaffLayout>
      <div className="flex justify-between items-center">
        <div>
          <h1 className="font-display text-4xl">Reviews</h1>
          <p className="text-sm text-[#71717A] mt-1">Approve guest submissions and curate featured reviews.</p>
        </div>
        <button onClick={() => setShowAdd(true)} data-testid="staff-add-review" className="bg-brand-primary text-white px-5 py-2.5 rounded-sm text-sm flex items-center gap-2">
          <Plus size={14} /> Add review
        </button>
      </div>

      <div className="mt-6 flex gap-2 text-xs">
        {TABS.map((t) => (
          <button
            key={t.key}
            data-testid={`reviews-tab-${t.key}`}
            onClick={() => setTab(t.key)}
            className={`px-3 py-1.5 rounded-sm uppercase tracking-wider ${tab === t.key ? "bg-brand-primary text-white" : "bg-white border border-[#E4E4E7]"}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="mt-6 space-y-4">
        {reviews.length === 0 && <div className="bg-white border border-[#E4E4E7] rounded-md p-8 text-center text-[#71717A]">No reviews here.</div>}
        {reviews.map((r) => (
          <div key={r.id} data-testid={`staff-review-${r.id}`} className="bg-white border border-[#E4E4E7] rounded-md p-5">
            <div className="flex justify-between gap-4 mb-3">
              <div>
                <div className="flex items-center gap-2">
                  {[1, 2, 3, 4, 5].map((n) => (
                    <Star key={n} size={14} className={n <= r.rating ? "fill-brand-primary text-brand-primary" : "text-brand-border"} />
                  ))}
                  {r.featured && <span className="text-[10px] uppercase tracking-wider text-brand-primary">Featured</span>}
                  <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-sm ${
                    r.status === "approved" ? "bg-emerald-100 text-emerald-700" :
                    r.status === "rejected" ? "bg-rose-100 text-rose-700" : "bg-amber-100 text-amber-700"
                  }`}>{r.status}</span>
                </div>
                <div className="mt-2 font-medium">{r.title || <em className="text-[#71717A]">No title</em>}</div>
                <div className="text-sm text-[#52525B] mt-1">{r.content}</div>
                <div className="text-xs text-[#71717A] mt-3">
                  {r.author_name}{r.country ? ` · ${r.country}` : ""}{r.room_name ? ` · ${r.room_name}` : ""} · {r.source}
                </div>
              </div>
              <div className="flex flex-col gap-2 shrink-0">
                {r.status !== "approved" && (
                  <button data-testid={`approve-${r.id}`} onClick={() => moderate(r.id, { status: "approved" })} className="text-xs text-emerald-700 flex items-center gap-1"><Check size={13} /> Approve</button>
                )}
                {r.status !== "rejected" && (
                  <button data-testid={`reject-${r.id}`} onClick={() => moderate(r.id, { status: "rejected" })} className="text-xs text-rose-700 flex items-center gap-1"><X size={13} /> Reject</button>
                )}
                <button data-testid={`feature-${r.id}`} onClick={() => moderate(r.id, { featured: !r.featured })} className="text-xs text-brand-primary flex items-center gap-1">
                  <Star size={13} /> {r.featured ? "Unfeature" : "Feature"}
                </button>
                <button data-testid={`edit-${r.id}`} onClick={() => setEditing(r)} className="text-xs text-[#52525B] flex items-center gap-1"><Pencil size={13} /> Edit</button>
                <button data-testid={`del-${r.id}`} onClick={() => del(r.id)} className="text-xs text-rose-700 flex items-center gap-1"><Trash2 size={13} /> Delete</button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {showAdd && <AddModal onClose={() => setShowAdd(false)} onCreated={() => { setShowAdd(false); load(); }} />}
      {editing && <EditModal review={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </StaffLayout>
  );
}

function EditModal({ review, onClose, onSaved }) {
  const [f, setF] = useState({
    author_name: review.author_name, country: review.country || "",
    rating: review.rating, title: review.title || "", content: review.content,
  });
  async function submit(e) {
    e.preventDefault();
    await api.patch(`/admin/reviews/${review.id}`, f);
    toast.success("Saved");
    onSaved();
  }
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <form onSubmit={submit} className="bg-white w-full max-w-lg rounded-md">
        <div className="p-5 border-b flex justify-between items-center">
          <h2 className="font-display text-2xl">Edit review</h2>
          <button type="button" onClick={onClose} className="p-2"><X size={18} /></button>
        </div>
        <div className="p-6 space-y-4 text-sm">
          <Field label="Author name"><input data-testid="edit-author" value={f.author_name} onChange={(e) => setF({ ...f, author_name: e.target.value })} className="berdhill-i" /></Field>
          <Field label="Country"><input value={f.country} onChange={(e) => setF({ ...f, country: e.target.value })} className="berdhill-i" /></Field>
          <Field label="Rating">
            <select value={f.rating} onChange={(e) => setF({ ...f, rating: Number(e.target.value) })} className="berdhill-i">
              {[5, 4, 3, 2, 1].map((n) => <option key={n} value={n}>{n} ★</option>)}
            </select>
          </Field>
          <Field label="Title"><input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} className="berdhill-i" /></Field>
          <Field label="Content"><textarea data-testid="edit-content" required rows={5} value={f.content} onChange={(e) => setF({ ...f, content: e.target.value })} className="berdhill-i" /></Field>
          <style>{`.berdhill-i{width:100%;border:1px solid #E4E4E7;border-radius:.125rem;padding:.5rem .75rem;font-size:.875rem;background:#fff}`}</style>
        </div>
        <div className="p-5 border-t flex justify-end gap-3">
          <button type="button" onClick={onClose} className="px-5 py-2.5 text-sm">Cancel</button>
          <button data-testid="edit-review-save" type="submit" className="bg-brand-primary text-white px-6 py-2.5 rounded-sm text-sm">Save changes</button>
        </div>
      </form>
    </div>
  );
}

function AddModal({ onClose, onCreated }) {
  const [f, setF] = useState({
    author_name: "", country: "", rating: 5, title: "", content: "", status: "approved", featured: false,
  });
  async function submit(e) {
    e.preventDefault();
    await api.post("/admin/reviews", f);
    toast.success("Review added");
    onCreated();
  }
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <form onSubmit={submit} className="bg-white w-full max-w-lg rounded-md">
        <div className="p-5 border-b flex justify-between items-center">
          <h2 className="font-display text-2xl">Add curated review</h2>
          <button type="button" onClick={onClose} className="p-2"><X size={18} /></button>
        </div>
        <div className="p-6 space-y-4 text-sm">
          <Field label="Author name"><input data-testid="add-author" required value={f.author_name} onChange={(e) => setF({ ...f, author_name: e.target.value })} className="berdhill-i" /></Field>
          <Field label="Country"><input value={f.country} onChange={(e) => setF({ ...f, country: e.target.value })} className="berdhill-i" /></Field>
          <Field label="Rating">
            <select value={f.rating} onChange={(e) => setF({ ...f, rating: Number(e.target.value) })} className="berdhill-i">
              {[5, 4, 3, 2, 1].map((n) => <option key={n} value={n}>{n} ★</option>)}
            </select>
          </Field>
          <Field label="Title"><input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} className="berdhill-i" /></Field>
          <Field label="Content"><textarea required rows={4} value={f.content} onChange={(e) => setF({ ...f, content: e.target.value })} className="berdhill-i" /></Field>
          <label className="flex items-center gap-2"><input type="checkbox" checked={f.featured} onChange={(e) => setF({ ...f, featured: e.target.checked })} /> Feature on homepage</label>
          <style>{`.berdhill-i{width:100%;border:1px solid #E4E4E7;border-radius:.125rem;padding:.5rem .75rem;font-size:.875rem;background:#fff}`}</style>
        </div>
        <div className="p-5 border-t flex justify-end gap-3">
          <button type="button" onClick={onClose} className="px-5 py-2.5 text-sm">Cancel</button>
          <button data-testid="add-review-save" type="submit" className="bg-brand-primary text-white px-6 py-2.5 rounded-sm text-sm">Add review</button>
        </div>
      </form>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-xs uppercase tracking-wider text-[#71717A]">{label}</span>
      <div className="mt-1">{children}</div>
    </label>
  );
}
